import { type JSX, useCallback, useMemo } from "react";

import dayjs from "dayjs";
import type { SeriesLineOptions } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import type { MarketResearchDemandAnalysisCommonModelsChangeValue } from "@similarweb/pro-api-client";
import {
    shortMonthAndYear,
    shortMonthDayAndYearWithDayOfWeek,
    shortMonthAndDay,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip } from "@similarweb/ui-components";
import { Box, Stack } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { type TimeGranularity, toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import {
    TOTAL_KEYWORD_LIST_ID,
    TOTAL_KEYWORD_LIST_COLOR,
    OTHER_KEYWORD_LIST_COLOR,
    useKeywordLists,
} from "app/modules/demand-analysis/keyword-lists";
import DurationServiceSingleton from "app/services/DurationService";
import { PeriodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";

import { ChartTooltipFootnote } from "../../components/ChartTooltipFootnote";
import { type ChartTooltipColumnDefinition, ChartTooltipWrapper } from "../../components/ChartTooltipWrapper";
import { DemandAnalysisLineChart, chartOptions } from "../../components/DemandAnalysisLineChart";
import { formatNumber, formatPercent } from "../../utils/numberFormatters";

import { getSecondaryColorPattern } from "./helpers";
import type { KeywordListData, LineChartTooltipRecord } from "./useKeywordListData";

const formatDate = (date: string | number | undefined, format = shortMonthAndYear) => {
    if (!date) {
        return "";
    }

    return dayjs(date).format(format);
};

const getColumnDefinition = (
    currentPeriodTitle: string,
    comparedPeriodTitle: string,
    periodOverPeriodType: IPeriodOverPeriodCompareType | null,
) => {
    const columnDefinition: Array<ChartTooltipColumnDefinition<LineChartTooltipRecord>> = [
        {
            key: "keywordList",
            title: translate("demand.analysis.search.trends.lineChart.keywordList.title"),
            align: "left",
            borderRight: true,
            selector: "keywordList",
            size: "175px",
            render: ({ keywordList }) => (
                <ChartTooltip.LabelCell
                    label={keywordList.name}
                    backgroundColor={keywordList.color}
                />
            ),
        },
        {
            key: "comparedVolume",
            title:
                periodOverPeriodType != null
                    ? comparedPeriodTitle
                    : translate("demand.analysis.search.trends.lineChart.volume.title"),
            align: "right",
            borderRight: true,
            selector: "comparedVolume",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({ comparedVolume }) => (
                <div data-automation="chart-tooltip-value">
                    <ChartTooltip.ValueCell value={formatNumber(comparedVolume)} />
                </div>
            ),
        },
        {
            key: "volume",
            title:
                periodOverPeriodType != null
                    ? currentPeriodTitle
                    : translate("demand.analysis.search.trends.lineChart.volume.title"),
            align: "right",
            borderRight: true,
            selector: "volume",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({ volume }) => (
                <div data-automation="chart-tooltip-value">
                    <ChartTooltip.ValueCell value={formatNumber(volume)} />
                </div>
            ),
        },
        {
            key: "volumeChange",
            title: translate(
                periodOverPeriodType == null
                    ? "demand.analysis.search.trends.lineChart.change.title.mom"
                    : periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.PERIOD
                      ? "demand.analysis.search.trends.lineChart.change.title.pop"
                      : "demand.analysis.search.trends.lineChart.change.title.yoy",
            ),
            align: "right",
            borderRight: false,
            selector: periodOverPeriodType == null ? "monthlyVolumeChange" : "periodVolumeChange",
            size: ChartTooltip.ChangeCellDefaultWidth,
            render: ({
                monthlyVolumeChange,
                periodVolumeChange,
            }: {
                monthlyVolumeChange?: MarketResearchDemandAnalysisCommonModelsChangeValue;
                periodVolumeChange?: MarketResearchDemandAnalysisCommonModelsChangeValue;
            }) => {
                const volumeChange = monthlyVolumeChange ?? periodVolumeChange;
                return (
                    <div data-automation="chart-tooltip-value">
                        {volumeChange !== undefined ? (
                            <ChartTooltip.ChangeCell
                                value={formatPercent(Math.abs(volumeChange.change))}
                                isDecrease={volumeChange.change < 0}
                            />
                        ) : (
                            <ChartTooltip.ValueCell value={NO_DATA_SIGN} />
                        )}
                    </div>
                );
            },
        },
    ];

    if (periodOverPeriodType == null) {
        return columnDefinition.filter((column) => column?.key !== "comparedVolume");
    }

    return columnDefinition;
};

type Props = {
    enabledKeywordGroups: Set<string | symbol>;
    height?: number;
    timeGranularity: TimeGranularity;
    data: KeywordListData;
};

export const KeywordsTrendsWidgetLineChart = ({
    enabledKeywordGroups,
    height,
    timeGranularity = "Monthly",
    data,
}: Props): JSX.Element => {
    const { getKeywordListById } = useKeywordLists();
    const periodOverPeriodType = DurationServiceSingleton.getPeriodOverPeriodCompareType();

    const getColor = useCallback(
        (id: string) => {
            if (id === TOTAL_KEYWORD_LIST_ID) {
                return TOTAL_KEYWORD_LIST_COLOR;
            }

            return getKeywordListById(id)?.color ?? OTHER_KEYWORD_LIST_COLOR;
        },
        [getKeywordListById],
    );

    const { lineChartData, lineChartTooltipData, lineChartCategories } = data;

    const series = useMemo(() => {
        return (
            lineChartData?.map((lineChartDataItem, index) => {
                const color = getColor(lineChartDataItem.id);

                return {
                    color,
                    type: "line",
                    id: `${String(lineChartDataItem.id)}-${index}`,
                    name: lineChartDataItem.name,
                    data: lineChartDataItem.data,
                    visible: enabledKeywordGroups.has(lineChartDataItem.id),
                    dashStyle: lineChartDataItem.isPeriodOverPeriod ? "LongDash" : "Solid",
                    zoneAxis: "x",
                    zones:
                        periodOverPeriodType == null && lineChartDataItem.monthToDateIndex > 0
                            ? [{ value: lineChartDataItem.monthToDateIndex - 1 }, { dashStyle: "Dot" }]
                            : undefined,
                    marker: {
                        enabled: lineChartDataItem.data.length === 1,
                        fillColor: lineChartDataItem.isPeriodOverPeriod ? getSecondaryColorPattern(color) : color,
                    },
                } satisfies SeriesLineOptions;
            }) ?? []
        );
    }, [lineChartData, getColor, enabledKeywordGroups, periodOverPeriodType]);

    const options = useMemo(() => {
        const timeGranularityConfig = toggleItems[timeGranularity];

        return chartOptions({
            chart: { height },
            xAxis: {
                categories: lineChartCategories,
                labels: {
                    formatter() {
                        return formatDate(this.value, timeGranularityConfig.format);
                    },
                },
            },
            tooltip: {
                formatter() {
                    const selectedDate = this.category;

                    if (
                        !selectedDate ||
                        typeof selectedDate !== "string" ||
                        !lineChartTooltipData ||
                        !lineChartTooltipData[selectedDate]
                    ) {
                        return null;
                    }

                    const entry = lineChartTooltipData[selectedDate];
                    const isDaily = timeGranularity === "Daily";
                    const formattedSelectedDate = formatDate(
                        selectedDate,
                        isDaily ? shortMonthDayAndYearWithDayOfWeek[0] : shortMonthAndYear,
                    );
                    const untilDate = formatDate(swSettings.current.windowEndDate, shortMonthAndDay);

                    let title = formattedSelectedDate;
                    if (entry.isMonthToDate) {
                        title = translate("demand_analysis.search_volume_over_time.tooltip.title_until_date", {
                            title: formattedSelectedDate,
                            untilDate,
                        });
                    }

                    const comparedPeriodTitle = formatDate(entry.comparedDate, shortMonthAndYear);
                    const visibleRecords = entry.records
                        .map((record) => ({
                            ...record,
                            keywordList: {
                                ...record.keywordList,
                                color: getColor(record.keywordList.id),
                            },
                        }))
                        .filter((item) => enabledKeywordGroups.has(item.keywordList.id));
                    const totalVolume = entry.totalVolume;

                    let footnote: string | undefined;
                    if (entry.isMonthToDate) {
                        if (periodOverPeriodType != null && entry.comparedDate) {
                            const onDate = formatDate(selectedDate, shortMonthAndYear);
                            footnote = translate(
                                "demand_analysis.search_volume_over_time.tooltip.footnote_partial_data.on_date",
                                { onDate, untilDate },
                            );
                        } else {
                            footnote = translate(
                                "demand_analysis.search_volume_over_time.tooltip.footnote_partial_data.simple",
                            );
                        }
                    }

                    return renderToStaticMarkup(
                        <Box
                            padding="16px"
                            borderRadius="8px"
                            background={colorsPalettes.carbon[0]}
                        >
                            <ChartTooltipWrapper<LineChartTooltipRecord>
                                title={title}
                                data={visibleRecords}
                                columnDefinition={getColumnDefinition(
                                    formattedSelectedDate,
                                    comparedPeriodTitle,
                                    periodOverPeriodType,
                                )}
                            >
                                <Stack gap="16px">
                                    {totalVolume > 0 ? (
                                        <ChartTooltip.TooltipFooterWithValue
                                            label={translate("common.total")}
                                            value={formatNumber(totalVolume)}
                                        />
                                    ) : null}
                                    {footnote ? <ChartTooltipFootnote>{footnote}</ChartTooltipFootnote> : null}
                                </Stack>
                            </ChartTooltipWrapper>
                        </Box>,
                    );
                },
            },
        });
    }, [
        height,
        lineChartCategories,
        lineChartTooltipData,
        timeGranularity,
        getColor,
        enabledKeywordGroups,
        periodOverPeriodType,
    ]);

    // TODO: Find out why the key is needed to force re-render of the graph
    const chartKey = Array.from(enabledKeywordGroups)
        .map((id) => id.toString())
        .join("-");

    return (
        <div
            data-automation="line-graph"
            key={chartKey}
        >
            <DemandAnalysisLineChart
                options={options}
                series={series}
            />
        </div>
    );
};
